import { useEffect, useMemo, useState } from "react";
import type { PublicMeetingDTO } from "../../shared/types";
import { Icon } from "../components/Icon";
import { Markdown } from "../components/Markdown";
import { MeetingPlayer } from "../components/MeetingPlayer";

/** A viewer id per browser tab, so the viewer count is people, not requests. */
function useViewerId() {
  return useMemo(() => {
    try {
      const key = "pandas-meeting-viewer";
      let id = sessionStorage.getItem(key);
      if (!id) {
        id = crypto.randomUUID();
        sessionStorage.setItem(key, id);
      }
      return id;
    } catch {
      return crypto.randomUUID();
    }
  }, []);
}

const dateTime = (iso: string) => new Date(iso).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const minutesBetween = (from: string, to: string) => Math.max(1, Math.round((Date.parse(to) - Date.parse(from)) / 60_000));

/** The public page of a published meeting (/m/<token>): no sign-in, updates live. */
export function PublicMeetingPage({ token }: { token: string }) {
  const viewerId = useViewerId();
  const [view, setView] = useState<PublicMeetingDTO | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let stopped = false;
    const load = async () => {
      const res = await fetch(`/api/public/meetings/${encodeURIComponent(token)}?viewer=${viewerId}`, { credentials: "omit" }).catch(() => null);
      if (stopped || !res) return;
      if (res.status === 404) {
        setMissing(true);
        setView(null);
        return;
      }
      if (res.ok) {
        setMissing(false);
        setView(await res.json());
      }
    };
    void load();
    const timer = setInterval(load, 5000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [token, viewerId]);

  useEffect(() => {
    if (view?.title) document.title = `${view.title} · Pandas AgentOS`;
  }, [view?.title]);

  if (missing) {
    return (
      <div className="grid min-h-dvh place-items-center bg-canvas px-4">
        <div className="card max-w-sm p-8 text-center">
          <img src="/favicon.png" alt="Pandito" className="mx-auto mb-3 size-14 rounded-2xl shadow-sm ring-1 ring-line" />
          <h1 className="text-heading font-semibold tracking-tight">This meeting page is not available</h1>
          <p className="mt-1 text-sm text-muted">The link may have been turned off by the people who shared it.</p>
        </div>
      </div>
    );
  }

  const started = view?.joinedAt ?? view?.createdAt ?? null;
  return (
    <div className="min-h-dvh bg-canvas text-fg">
      <header className="glass sticky top-0 z-20 border-b border-line">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3">
          <img src="/favicon-32.png" alt="" className="size-8 shrink-0 rounded-lg shadow-sm ring-1 ring-line" />
          <div className="min-w-0 flex-1">
            <div className="text-tiny font-medium tracking-wide text-faint uppercase">Pandas meeting</div>
            <h1 className="truncate text-heading font-semibold tracking-tight sm:text-base">{view?.title || (view ? "Meeting" : "Loading…")}</h1>
          </div>
          {view?.live && (
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-danger/10 px-2.5 py-1 text-xs font-medium text-danger">
              <span className="size-1.5 animate-pulse rounded-full bg-danger" /> Live
            </span>
          )}
          {view && (
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-surface px-2.5 py-1 text-xs font-medium text-muted shadow-sm ring-1 ring-line" title="People on this page now">
              <Icon name="users" className="size-3.5" />
              {view.viewers}
              <span className="hidden sm:inline">{view.viewers === 1 ? "viewer" : "viewers"}</span>
            </span>
          )}
        </div>
      </header>

      {!view ? (
        <main className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-6 lg:grid-cols-2">
          <div className="card h-96 animate-pulse" />
          <div className="card h-96 animate-pulse" />
        </main>
      ) : (
        <main className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
          <section className="min-w-0 space-y-3">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
              {started && <span>{dateTime(started)}</span>}
              {started && view.endedAt && <span>{minutesBetween(started, view.endedAt)} min</span>}
              {view.live && <span>In progress</span>}
            </div>
            <h2 className="text-heading font-semibold tracking-tight">Minutes</h2>
            <div className="card p-5">
              {view.minutes ? (
                <Markdown>{view.minutes}</Markdown>
              ) : (
                <p className="text-sm text-muted">
                  {view.live
                    ? "The minutes are written when the meeting ends. Follow the recording and transcript live on this page."
                    : view.status === "processing"
                      ? "The minutes are being written and appear here in a few minutes."
                      : "No minutes for this meeting."}
                </p>
              )}
            </div>
          </section>

          <section className="min-w-0 space-y-3 lg:sticky lg:top-20 lg:self-start">
            <h2 className="text-heading font-semibold tracking-tight">Recording and transcript</h2>
            <MeetingPlayer
              chunks={view.audioChunks}
              frames={view.frames}
              transcript={view.transcript}
              live={view.live}
              startAt={started ?? view.createdAt}
              fileHref={(id) => `/api/public/meetings/${encodeURIComponent(token)}/files/${id}`}
            />
          </section>
        </main>
      )}

      <footer className="mx-auto max-w-7xl px-4 pb-8 text-center text-xs text-faint">Shared from AgentOS</footer>
    </div>
  );
}
